import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FiMenu, FiX, FiGlobe } from "react-icons/fi";
import { useTranslation } from 'react-i18next';

// --- Styled Components (Restored Layout) ---
const Nav = styled.nav`
  background: #333;
  padding: 1rem 1.5rem; /* Original padding */
  display: flex;
  justify-content: space-between; /* Original alignment */
  align-items: center;
  position: relative; /* Was relative */
  height: 60px;
  z-index: 1100;
`;

// Updated LogoLink to include image styling
const LogoLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-size: 1.5rem;
  font-weight: bold;
  transition: color 0.3s;
  display: flex;        /* Make it a flex container */
  align-items: center; /* Vertically align items */
  gap: 0.5rem;          /* Add space between image and text */

  &:hover {
    color: #f4a261;
  }

  /* Style the image within the link */
  img {
    height: 40px;     /* Adjust height as needed */
    width: auto;      /* Maintain aspect ratio */
    display: block; /* Prevents extra space below image */
  }
`;

const MenuIcon = styled.div`
  display: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
    order: 3; // Ensure it's on the far right on mobile
  }
`;

const NavLinks = styled.ul`
  list-style: none;
  display: flex;
  gap: 1.5rem; /* Original gap */
  margin: 0;
  padding: 0;
  align-items: center; /* Keep items vertically aligned */

  @media (max-width: 768px) {
    flex-direction: column;
    position: fixed;
    top: 60px; // Start below the navbar height
    right: 0;
    height: calc(100% - 60px); // Full height minus navbar
    width: 70%; // Original width
    background: #222; // Original mobile menu background
    padding: 2rem; // Original padding
    transition: transform 0.3s ease-in-out;
    transform: ${({ open }) => (open ? "translateX(0)" : "translateX(100%)")};
    box-shadow: -2px 0 5px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    align-items: flex-start; // Align links to the left in mobile menu
    gap: 0; // Reset gap for column layout, handled by NavItem padding
  }
`;

const NavItem = styled.li`
  display: inline-block; // Original display

  @media (max-width: 768px) {
    display: block; // Original mobile display
    padding: 1rem 0; // Original mobile padding
    width: 100%;
  }
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-size: 1rem; // Original font size
  font-weight: bold; // Original font weight
  transition: color 0.3s;

  &:hover {
    color: #f4a261;
  }

  @media (max-width: 768px) {
    font-weight: 500; // Slightly lighter for mobile menu if desired
    display: block; // Make link take full width of NavItem
  }
`;

const QuoteButton = styled(Link)`
  background: #f4a261;
  color: #333;
  text-decoration: none;
  font-size: 1rem;
  font-weight: bold;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  transition: all 0.3s;
  display: inline-block;
  margin-left: 1rem;

  &:hover {
    background: #e89451;
    color: white;
    box-shadow: 0 4px 8px rgba(244, 162, 97, 0.3);
  }

  @media (max-width: 768px) {
    margin-left: 0;
    display: block;
    text-align: center;
    width: 100%;
  }
`;

const Overlay = styled.div`
  display: ${({ open }) => (open ? "block" : "none")};
  position: fixed;
  top: 60px; // Start below navbar
  left: 0;
  width: 100%;
  height: calc(100% - 60px);
  background: rgba(0, 0, 0, 0.5);
  z-index: 999; // Below mobile menu
`;

// Style for the language switcher button - Placed within NavLinks on desktop
const LangButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1.1rem; // Slightly smaller than menu icon for desktop integration
  display: flex;
  align-items: center;
  padding: 0 0 0 0.5rem; // Add padding left to space from last link
  transition: color 0.3s;
  margin-left: 0.5rem; // Space from previous item

  &:hover {
    color: #f4a261;
  }

  // Style for when button is inside the mobile menu (within NavItem)
  ${NavItem} & {
      font-size: 1rem; // Consistent size in mobile menu
      padding: 0; // Reset padding, handled by NavItem
      width: 100%;
      justify-content: flex-start; // Align left in mobile menu
      font-weight: 500; // Match mobile NavLink weight
      gap: 10px; // Space between icon and text
      margin-left: 0; // Reset margin
  }
`;

// Container for right-side controls on mobile ONLY
const MobileControls = styled.div`
  display: none; // Hidden on desktop
  @media (max-width: 768px) {
      display: flex;
      align-items: center;
      gap: 1rem; // Space between lang and menu icon if lang icon was here
  }
`;

// --- End Styled Components ---

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const changeLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
    setMenuOpen(false);
  };

  const handleLinkClick = () => {
    setMenuOpen(false);
  };

  return (
    <>
      <Nav>
        {/* Logo - Updated to include the image */}
        <LogoLink to="/" onClick={handleLinkClick}>
          <img src="/resources/images/logo.png" alt="ImraneLabs Logo" /> {/* <-- Added image */}
          ImraneLabs
        </LogoLink>

        {/* Navigation Links + Lang Button (Desktop) */}
        {/* This whole UL is hidden and replaced by the slide-in menu on mobile */}
        <NavLinks open={menuOpen}>
          {/* Mobile Only: Language Switcher first */}
          <NavItem className="mobile-only-lang">
             <LangButton onClick={changeLanguage}>
                <FiGlobe />
                <span style={{ marginLeft: '10px' }}>
                    {i18n.language === 'en' ? 'Français' : 'English'}
                </span>
             </LangButton>
             <style>{`.mobile-only-lang { display: none } @media (max-width: 768px) { .mobile-only-lang { display: block; } }`}</style>
          </NavItem>

          {/* Links */}
          <NavItem>
            <NavLink to="/" onClick={handleLinkClick}> {t('navbar.home')} </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/about" onClick={handleLinkClick}> {t('navbar.about')} </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/services" onClick={handleLinkClick}> {t('navbar.services')} </NavLink>
          </NavItem>
           <NavItem>
            <NavLink to="/pricing" onClick={handleLinkClick}> {t('navbar.pricing')} </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/portfolio" onClick={handleLinkClick}> {t('navbar.portfolio')} </NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/contact" onClick={handleLinkClick}> {t('navbar.contact')} </NavLink>
          </NavItem>
          
          {/* Request Quote Button */}
          <NavItem>
            <QuoteButton to="/contact" onClick={handleLinkClick}>
              {t('navbar.requestQuote')}
            </QuoteButton>
          </NavItem>

          {/* Desktop Only: Language Switcher */}
          <NavItem className="desktop-only-lang">
             <LangButton onClick={changeLanguage} title={t('navbar.switchLanguage')}>
                <FiGlobe />
                 <span style={{fontSize: '0.8rem', marginLeft: '0px', fontWeight: 'normal'}}>
                     {i18n.language === 'en' ? 'FR' : 'EN'}
                 </span>
             </LangButton>
             {/* Simple CSS to hide on mobile */}
             <style>{`.desktop-only-lang { display: list-item; } @media (max-width: 768px) { .desktop-only-lang { display: none; } }`}</style>
          </NavItem>
        </NavLinks>

         {/* Mobile Only: Menu Icon */}
         <MobileControls>
           {/* Hamburger Menu Icon */}
           <MenuIcon onClick={() => setMenuOpen(!menuOpen)}>
             {menuOpen ? <FiX /> : <FiMenu />}
           </MenuIcon>
         </MobileControls>

      </Nav>

      {/* Overlay (Mobile) */}
      <Overlay open={menuOpen} onClick={() => setMenuOpen(false)} />
    </>
  );
};

export default Navbar;